import { CreateTableState, Icon, Tooltip, Typography } from 'gu-ui-kit'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from 'modules/MediaPlanner/MediaPlanner.module.scss'
import clsx from 'clsx'
import { formatByLocale } from 'utils/formatMoney'
import { useSelector } from 'react-redux'
import { isNumber } from 'lodash'

export const useTableConfig = ({ data = {}, summary = {}, config, columnVisibility }) => {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState([])
  const { i18n } = useSelector(({ app }) => app)

  const prepareNumber = useCallback(
    (n) =>
      formatByLocale(n, i18n, {
        maximumFractionDigits: 2,
      }),
    [],
  )

  const tableConfig = useMemo(() => {
    const { tableConfig } = config?.['target_results'] ?? {}

    return {
      columns: Object.entries(tableConfig?.columns ?? {}).map(
        ([id, { postfix = null, tooltip = false, ...props }], index) => ({
          ...props,
          id,
          className: clsx(
            styles['media-planner__table-cell'],
            styles[`media-planner__table-cell_${id}`],
          ),
          header: (
            <>
              <Typography
                component={'span'}
                variant={'Body-20'}
                color={'Label-Fifth'}
              >
                {t(`media_planner::table.header.${id}.label`)}
                {postfix ? `, ${postfix}` : null}
              </Typography>
              {tooltip ? (
                <Tooltip
                  className={styles['media-planner__tooltip']}
                  title={t(`media_planner::table.header.${id}.tooltip`)}
                >
                  <Icon
                    size={'sm'}
                    name={'information'}
                    color={'Interface-Seventh'}
                  />
                </Tooltip>
              ) : null}
            </>
          ),
          cell: ({ row: { original = {} } }) => {
            const value = original?.[id] ?? ''

            return (
              <Typography
                component={'span'}
                variant={'Body-Forms-15'}
                color={'Label-Fifth'}
                noWrap
              >
                {isNumber(value) ? prepareNumber(value) : value}
                {postfix ? ` ${postfix}` : null}
              </Typography>
            )
          },
          footer: (
            <Typography
              component={'span'}
              variant={'Subtitle-20'}
              color={'Label-Fifth'}
              noWrap
            >
              {index === 0
                ? t(`media_planner::table.footer.${id}.label`)
                : summary?.[id]
                ? prepareNumber(summary[id]) + (postfix ? ` ${postfix}` : '')
                : ''}
            </Typography>
          ),
        }),
      ),
      sorting: tableConfig.sorting,
      state: {
        columnVisibility,
        sorting,
      },
      showHeader: data?.length > 0,
      showFooter: data?.length > 0,
      className: styles['media-planner__table'],
      onSortingChange: setSorting,
    }
  }, [columnVisibility, data, sorting, config])

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
    sorting,
  }
}
